import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import { useAuthStore } from '../../../stores/authStore'
import api from '../../../lib/api'

export default function LoginPage() {
  const navigate = useNavigate()
  const { setOtpSent } = useAuthStore()
  const [identifier, setIdentifier] = useState('')
  const [method, setMethod] = useState<'email' | 'phone'>('phone')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await api.post('/auth/request-otp', {
        identifier: method === 'phone' ? `+91${identifier.replace(/\D/g, '')}` : identifier,
        type: method,
      })

      setOtpSent(identifier, method)
      navigate('/auth/verify')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      setError(error.response?.data?.detail || 'Failed to send OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const isValidInput = method === 'phone'
    ? /^\d{10}$/.test(identifier.replace(/\D/g, ''))
    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 text-white rounded-2xl mb-4 shadow-lg">
            <i className="fas fa-tooth text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Jasmine Dental Lab</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Lab Operations & Billing System</p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Sign in to your account</h2>

          {/* Method Toggle */}
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-6">
            <button
              type="button"
              onClick={() => { setMethod('phone'); setIdentifier(''); setError('') }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                method === 'phone'
                  ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <i className="fas fa-phone mr-2"></i>
              Phone
            </button>
            <button
              type="button"
              onClick={() => { setMethod('email'); setIdentifier(''); setError('') }}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                method === 'email'
                  ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <i className="fas fa-envelope mr-2"></i>
              Email
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {method === 'phone' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-l-lg text-sm">
                    +91
                  </span>
                  <input
                    type="tel"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="9876543210"
                    className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  We'll send a 4-digit OTP to this number
                </p>
              </div>
            ) : (
              <Input
                label="Email Address"
                type="email"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@example.com"
                helperText="We'll send a 4-digit OTP to this email"
                autoFocus
              />
            )}

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  {error}
                </p>
              </div>
            )}

            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={!isValidInput}
            >
              <i className="fas fa-paper-plane mr-2"></i>
              Send OTP
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-center text-gray-500 dark:text-gray-400">
              By signing in, you agree to our{' '}
              <a href="/legal/terms" className="text-primary-600 hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="/legal/privacy" className="text-primary-600 hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>

        {/* Help */}
        <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-6">
          Need help? Contact{' '}
          <a href="mailto:support@jasminedental.com" className="text-primary-600 hover:underline">
            support@jasminedental.com
          </a>
        </p>
      </div>
    </div>
  )
}
